import { useEffect, useState } from 'react';

/**
 * The public API client.
 *
 * Read-only apart from the forms and the consent record. There is no token here
 * and never should be: everything this file can reach is already public.
 */

/**
 * Where the API lives. A production build talks to the live API unless
 * `VITE_API_URL` overrides it, so `pnpm build:citizensgavel` is correct with or
 * without an env file — every `.env.*` is gitignored, so a fresh checkout has none.
 */
const BASE =
    (import.meta.env.VITE_API_URL as string | undefined) ??
    (import.meta.env.PROD ? 'https://api.citizensgavel.org/api' : 'http://localhost:4000/api');

export class ApiError extends Error {
    status: number;
    /** Field-level messages from the server, shaped for a form. */
    fields?: Record<string, string>;

    constructor(status: number, message: string, fields?: Record<string, string>) {
        super(message);
        this.status = status;
        this.fields = fields;
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    let response: Response;
    try {
        response = await fetch(`${BASE}${path}`, {
            // Always ask the API rather than trust a cached copy: a feed the CMS
            // just changed must show on the next load. An unchanged feed costs a 304.
            cache: 'no-cache',
            ...init,
            // `Content-Type` only where there is a body to describe. On a GET it
            // says nothing and costs everything: it is not a CORS-safelisted
            // header, so the browser sends a preflight OPTIONS first and the feed
            // waits a whole round trip before it can even start.
            headers: { ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
        });
    } catch {
        throw new ApiError(0, 'Could not reach the server.');
    }

    const body = (await response.json().catch(() => null)) as
        | { error?: string; detail?: Record<string, string> }
        | T
        | null;

    if (!response.ok) {
        const payload = body as { error?: string; detail?: Record<string, string> } | null;
        throw new ApiError(response.status, payload?.error ?? 'Something went wrong.', payload?.detail);
    }
    return body as T;
}

export const api = {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
};

/**
 * Fetches a public feed, falling back to whatever the page already bundles.
 *
 * The site stays readable with the API down — a marketing page that renders
 * nothing because a fetch failed is worse than one showing slightly stale copy.
 * `stale` says which of the two you are looking at.
 */
export function useSiteData<T>(path: string, fallback: T): { data: T; loading: boolean; stale: boolean } {
    const [data, setData] = useState<T>(fallback);
    const [loading, setLoading] = useState(true);
    const [stale, setStale] = useState(true);

    useEffect(() => {
        let cancelled = false;

        api.get<T>(path)
            .then((live) => {
                if (cancelled) return;
                setData(live);
                setStale(false);
            })
            .catch(() => {
                // Keep the bundled copy; `stale` stays true so a caller can say so.
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [path]);

    return { data, loading, stale };
}
